import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import ReceiptIcon from "@material-ui/icons/Receipt";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import DeleteIcon from "@material-ui/icons/Delete";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import useConfirm from "../../hooks/useConfirm";
import useSocketSubscription from "../../hooks/useSocketSubscription";
import { displayClientName } from "../../utils/clientName";

export const ORDER_STATUS_META = {
  draft: { label: "Rascunho", tone: "neutral" },
  confirmed: { label: "Confirmado", tone: "info" },
  invoiced: { label: "Faturado", tone: "warning" },
  delivered: { label: "Entregue", tone: "success" },
  canceled: { label: "Cancelado", tone: "danger" },
};

export const fmtMoney = (v, currency = "BRL") =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency });

const useStyles = makeStyles((theme) => ({
  number: {
    fontFamily: "'SF Mono', 'Roboto Mono', Menlo, monospace",
    fontSize: 12,
    fontWeight: 600,
  },
  money: { fontVariantNumeric: "tabular-nums", fontWeight: 600 },
  muted: { color: theme.palette.text.hint },
  date: { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  sub: { color: theme.palette.text.secondary, display: "block" },
}));

const Orders = () => {
  const classes = useStyles();
  const history = useHistory();
  const [confirm, confirmDialog] = useConfirm();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", search: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.search) params.search = filter.search;
      const { data } = await api.get("/orders", { params });
      setOrders(data?.orders || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  // Faturar e cancelar mexem no financeiro; quem estiver com a lista aberta
  // precisa ver a mudança sem recarregar a página na mão.
  useSocketSubscription("order", load);

  const remove = async (order) => {
    const ok = await confirm({
      title: `Excluir o pedido ${order.number}?`,
      message:
        "O pedido sai da lista. Pedido já faturado não pode ser excluído — nesse caso, cancele.",
    });
    if (!ok) return;
    try {
      await api.delete(`/orders/${order.id}`);
      toast.success("Pedido excluído.");
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const columns = [
    {
      key: "number",
      label: "Número",
      render: (o) => <span className={classes.number}>{o.number}</span>,
    },
    {
      key: "client",
      label: "Cliente",
      render: (o) => (
        <div>
          <Typography variant="subtitle2" noWrap>
            {displayClientName(o.client, `#${o.clientId}`)}
          </Typography>
          {o.proposal?.number && (
            <Typography variant="caption" className={classes.sub}>
              Proposta {o.proposal.number}
            </Typography>
          )}
        </div>
      ),
    },
    {
      key: "status",
      label: "Situação",
      render: (o) => {
        const meta = ORDER_STATUS_META[o.status] || {
          label: o.status,
          tone: "neutral",
        };
        return <StatusChip dot tone={meta.tone} label={meta.label} />;
      },
    },
    {
      key: "totalAmount",
      label: "Total",
      align: "right",
      render: (o) => (
        <span className={classes.money}>
          {fmtMoney(o.totalAmount, o.currency)}
        </span>
      ),
    },
    {
      key: "installments",
      label: "Parcelas",
      align: "right",
      render: (o) =>
        Number(o.installments) > 1 ? (
          <span className={classes.date}>{o.installments}x</span>
        ) : (
          <span className={classes.muted}>à vista</span>
        ),
    },
    {
      key: "expectedDeliveryDate",
      label: "Entrega prevista",
      render: (o) =>
        o.expectedDeliveryDate ? (
          <span className={classes.date}>
            {new Date(`${o.expectedDeliveryDate}T12:00:00`).toLocaleDateString(
              "pt-BR"
            )}
          </span>
        ) : (
          <span className={classes.muted}>—</span>
        ),
    },
    {
      key: "owner",
      label: "Responsável",
      render: (o) => o.owner?.name || <span className={classes.muted}>—</span>,
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: (o) => (
        <>
          <Tooltip title="Abrir">
            <IconButton
              size="small"
              onClick={() => history.push(`/orders/${o.id}`)}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* Faturado e entregue não somem da lista: existe cobrança apontando
              para eles. O caminho é cancelar, que estorna o que está em aberto. */}
          {!["invoiced", "delivered"].includes(o.status) && (
            <Tooltip title="Excluir">
              <IconButton size="small" onClick={() => remove(o)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  return (
    <MainContainer>
      {confirmDialog}
      <PageHeader
        title="Pedidos"
        count={orders.length}
        subtitle="O que foi vendido, em quantas parcelas e para quando. Faturar aqui é o que gera as contas a receber."
        filters={
          <>
            <TextField
              label="Buscar por número"
              value={filter.search}
              onChange={(e) =>
                setFilter((f) => ({ ...f, search: e.target.value }))
              }
            />
            <TextField
              select
              label="Situação"
              style={{ minWidth: 180 }}
              value={filter.status}
              onChange={(e) =>
                setFilter((f) => ({ ...f, status: e.target.value }))
              }
            >
              <MenuItem value="">Todas</MenuItem>
              {Object.entries(ORDER_STATUS_META).map(([value, meta]) => (
                <MenuItem key={value} value={value}>
                  {meta.label}
                </MenuItem>
              ))}
            </TextField>
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={orders}
        loading={loading}
        onRowClick={(o) => history.push(`/orders/${o.id}`)}
        emptyIcon={<ReceiptIcon />}
        emptyTitle="Nenhum pedido"
        emptyDescription="Pedidos nascem quando o cliente aceita uma proposta. É neles que se define parcelamento e prazo de entrega antes de faturar."
      />
    </MainContainer>
  );
};

export default Orders;
